import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, MapPin, Briefcase, Building2, ArrowRight, Bookmark, BookmarkCheck, Sparkles, Bell, Star } from "lucide-react";
import { ProfileCompletenessWidget, RecommendedJobsSection } from "@/components/SeekerDashboardWidgets";

const SMART_MATCH_KEY = "seeker_smart_match_v1";

export default function SeekerJobs() {
    const [jobs, setJobs] = useState([]);
    const [scores, setScores] = useState({});  // jobId -> matchScore from recommended
    const [savedIds, setSavedIds] = useState(new Set());
    const [stats, setStats] = useState(null);
    const [q, setQ] = useState("");
    const [location, setLocation] = useState("");
    const [loading, setLoading] = useState(true);
    const [smartMatch, setSmartMatch] = useState(() => {
        try { return localStorage.getItem(SMART_MATCH_KEY) !== "off"; } catch { return true; }
    });
    const [hasSkills, setHasSkills] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (location) params.location = location;
            const [{ data: jobs }, savedRes, statsRes, recRes] = await Promise.all([
                api.get("/seeker/jobs", { params }),
                api.get("/seeker/saved-jobs").catch(() => ({ data: [] })),
                api.get("/seeker/dashboard-stats").catch(() => ({ data: null })),
                api.get("/seeker/recommended-jobs", { params: { limit: 50 } }).catch(() => ({ data: { items: [], has_skills: false } })),
            ]);
            setJobs(jobs || []);
            setSavedIds(new Set((savedRes.data || []).map((j) => j.id)));
            setStats(statsRes.data);
            setHasSkills(!!recRes.data?.has_skills);
            // Build a {job_id → match_score} map so the browse list can show chips + sort
            const map = {};
            for (const it of (recRes.data?.items || [])) {
                if (it.id) map[it.id] = it.match_score;
            }
            setScores(map);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const toggleSave = async (jid) => {
        const has = savedIds.has(jid);
        try {
            if (has) await api.delete(`/seeker/saved-jobs/${jid}`);
            else await api.post(`/seeker/saved-jobs/${jid}`);
            setSavedIds((cur) => {
                const next = new Set(cur);
                if (has) next.delete(jid); else next.add(jid);
                return next;
            });
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const submit = (e) => { e.preventDefault(); load(); };

    const toggleSmartMatch = () => {
        const next = !smartMatch;
        setSmartMatch(next);
        try { localStorage.setItem(SMART_MATCH_KEY, next ? "on" : "off"); } catch { /* ignore */ }
    };

    const saveCurrentSearch = async () => {
        const name = window.prompt("Name this alert (e.g. 'Senior React in Bangalore')", q || location || "My alert");
        if (!name) return;
        try {
            await api.post("/seeker/job-alerts", { name, keywords: q, location, active: true });
            toast.success("Job alert saved — we'll email you when new matches appear");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    // Apply Smart Match sort: when ON and seeker has skills, list ranked-first by match score
    const sortedJobs = useMemo(() => {
        if (!smartMatch || !hasSkills) return jobs;
        return [...jobs].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0));
    }, [jobs, scores, smartMatch, hasSkills]);

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-6 lg:p-8 space-y-6">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">For candidates</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Open roles right now</h1>
                <p className="text-muted-foreground mt-2">Browse jobs from companies actively hiring. Click any role to apply directly with your resume.</p>
            </div>

            <ProfileCompletenessWidget />

            {stats ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="seeker-stats-strip">
                    {[
                        { label: "Applications", value: stats.applications, to: "/seeker/applications" },
                        { label: "Interviews", value: stats.interviews, to: "/seeker/applications" },
                        { label: "Offers", value: stats.offers, to: "/seeker/applications" },
                        { label: "Saved jobs", value: stats.saved_jobs, to: "/seeker/saved" },
                    ].map((s) => (
                        <Link key={s.label} to={s.to} className="border border-border bg-white p-3 hover:border-ikb transition" data-testid={`seeker-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{s.label}</div>
                            <div className="text-2xl font-display font-bold text-ikb mt-1">{s.value}</div>
                        </Link>
                    ))}
                </div>
            ) : null}

            <RecommendedJobsSection savedIds={savedIds} onSaveToggle={toggleSave} />

            <form onSubmit={submit} className="border border-border bg-white p-5 grid md:grid-cols-12 gap-3">
                <div className="md:col-span-5 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search title, skill, keyword..."
                        className="rounded-none pl-10 h-10"
                        data-testid="seeker-jobs-q"
                    />
                </div>
                <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Location"
                    className="rounded-none h-10 md:col-span-3"
                    data-testid="seeker-jobs-location"
                />
                <Button type="submit" className="rounded-none h-10 bg-ikb hover:bg-[#001f7a] text-white md:col-span-2" data-testid="seeker-jobs-search">
                    Search
                </Button>
                <Button type="button" onClick={saveCurrentSearch} variant="outline" className="rounded-none h-10 md:col-span-2" data-testid="seeker-save-search">
                    <Bell className="w-3.5 h-3.5 mr-1" /> Save alert
                </Button>
            </form>

            {hasSkills ? (
                <div className="flex items-center gap-2 -mt-2">
                    <button
                        onClick={toggleSmartMatch}
                        className={`text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 border inline-flex items-center gap-1.5 ${smartMatch ? "border-ikb bg-ikb text-white" : "border-border bg-white text-foreground/70 hover:bg-secondary"}`}
                        data-testid="smart-match-toggle"
                        title={smartMatch ? "Smart match is ON — jobs ranked by your skills + location" : "Smart match is OFF — jobs sorted by newest"}
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        Smart match {smartMatch ? "on" : "off"}
                    </button>
                    <span className="text-[10px] font-mono text-foreground/50">
                        {smartMatch ? "Ranked by your skills + location" : "Sorted by newest"}
                    </span>
                </div>
            ) : null}

            {loading ? (
                <div className="text-sm text-muted-foreground font-mono">Loading...</div>
            ) : jobs.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center">
                    <h3 className="font-display font-bold text-xl">No matching roles right now</h3>
                    <p className="text-sm text-muted-foreground mt-2">Try broadening your search or clearing the location filter.</p>
                </div>
            ) : (
                <div className="border border-border bg-white divide-y divide-border">
                    {sortedJobs.map((j) => {
                        const saved = savedIds.has(j.id);
                        const matchPct = scores[j.id] ? Math.round(scores[j.id] * 100) : null;
                        return (
                            <div key={j.id} data-testid={`seeker-job-row-${j.id}`} className="block p-6 hover:bg-secondary transition-colors group">
                                <Link to={`/j/${j.slug}`} data-testid={`seeker-job-${j.id}`} className="block">
                                    <div className="flex items-start justify-between gap-6 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <h3 className="font-display font-bold text-lg group-hover:text-ikb transition-colors">{j.title}</h3>
                                                <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-0.5">{j.employment_type}</span>
                                                {smartMatch && matchPct ? (
                                                    <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-800 px-2 py-0.5 inline-flex items-center gap-1" data-testid={`match-chip-${j.id}`} title={`Match score ${matchPct}% based on your skills + location`}>
                                                        <Star className="w-3 h-3" /> {matchPct}% match
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="flex items-center gap-3 text-xs font-mono text-foreground/80 mt-2 flex-wrap">
                                                <span className="inline-flex items-center gap-1.5"><Building2 className="w-3 h-3" /> {(j.company || {}).name || "Confidential"}</span>
                                                <span>·</span>
                                                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {j.location || "Remote"}</span>
                                                <span>·</span>
                                                <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> {j.seniority || "—"} · {j.experience_min}–{j.experience_max} YoE</span>
                                            </div>
                                            {(j.required_skills || []).length > 0 && (
                                                <div className="flex flex-wrap gap-1.5 mt-3">
                                                    {(j.required_skills || []).slice(0, 6).map((s) => (
                                                        <span key={s} className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-0.5">{s}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-xs font-mono uppercase tracking-widest text-ikb group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                                            Apply <ArrowRight className="w-3 h-3" />
                                        </div>
                                    </div>
                                </Link>
                                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        onClick={() => toggleSave(j.id)}
                                        className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-1 inline-flex items-center gap-1 transition-colors ${saved ? "border-ikb bg-ikb text-white" : "border-border bg-white hover:bg-secondary"}`}
                                        data-testid={`save-toggle-${j.id}`}
                                    >
                                        {saved ? <><BookmarkCheck className="w-3 h-3" /> Saved</> : <><Bookmark className="w-3 h-3" /> Save</>}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
